import {MutableRefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import './grouped-tasks-handling.scss';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {
  Stack,
  Skeleton,
  SxProps,
  Theme,
  Typography,
  RadioGroup,
  Radio,
  Button,
  Collapse,
  Box,
  CircularProgress,
} from '@mui/material';
import {Controller, useForm} from 'react-hook-form';
import {DailyCareInput} from '../../../../components/daily-care-log/forms/daily-care-input';
import {RadioLabel} from '../../../../components/shared/forms/radio';
import {DailyCareSelect} from '../../../../components/daily-care-log/forms/daily-care-select';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import {useSnackBar} from '../../../../contexts/snack-bar/context';
import {EntityUserCareTasksRepository} from '../../../../repositories/entity-user-care-tasks';
import {CareTaskComplementary} from '../../../../backend/interfaces/care-task-complementary';
import {CareTaskNotDoneReason} from '../../../../backend/enums/care-task-not-done-reason';
import {PutEntityCareTask} from '../../../../backend/requests/put-entity-care-task';
import {CareTask} from '../../../../backend/interfaces/care-task';
import {ScreenPaths} from '../../../../enums';
import {DEFAULT_FORM_VALIDATIONS} from '../../../../utils/constants';

const titleSx: SxProps<Theme> = {
  color: 'var(--dcm-gray)',
  fontFamily: 'Roboto',
  fontSize: '18px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.225px',
};

const titleLoadingSkeletonSx: SxProps<Theme> = {
  height: '40px',
  width: '120px',
};

interface FormData {
  markedAsDone?: 'done' | 'not-done';
  taskNotDoneReason?: CareTaskNotDoneReason;
  notes?: string;
}

type LocationState = {
  tasks: CareTaskComplementary[];
};

const GroupedTasksHandlingScreen = () => {
  const [tasks, setTasks] = useState<CareTaskComplementary[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const {t} = useTranslation();

  const {entityId, entityShiftId}: {entityId: string; entityShiftId: string} = useParams();

  const dailyCareLogContext = useDailyCareLog();
  const snackBarContext = useSnackBar();
  const history = useHistory();
  const location = useLocation();
  const notesWrapper = useRef() as MutableRefObject<HTMLDivElement>;
  const searchParams = new URLSearchParams(location.search);
  const date = searchParams.get('date') ?? moment().format('YYYY-MM-DD');
  const taskRef = searchParams.get('taskRef') ?? '';
  const tasksRefs = useMemo(() => taskRef.split(','), [taskRef]);

  const {
    register,
    control,
    getValues,
    reset,
    watch,
    clearErrors,
    setError,
    setFocus,
    formState: {isValid, isDirty, errors},
  } = useForm<FormData>({
    ...DEFAULT_FORM_VALIDATIONS,
  });

  const fetchCareTasksInformation = useCallback(() => {
    const requests: Promise<CareTaskComplementary>[] = [];

    tasksRefs.forEach((taskRef) =>
      requests.push(EntityUserCareTasksRepository.instance.GetEntityCareTask(entityId, taskRef))
    );

    Promise.all<Promise<CareTaskComplementary>[]>(requests)
      .then((response) => {
        setTasks(response);
        setIsLoading(false);
      })
      .catch(() => {
        snackBarContext.setDailyCareLogToastNotification({
          severity: 'error',
          message: t(
            translations.daily_care_log.grouped_tasks_handling.snackbar.loading_tasks.error
          ),
        });

        history.goBack();
      });
  }, [entityId, history, snackBarContext, t, tasksRefs]);

  const fillFormValues = useCallback(() => {
    if (!tasks) return;

    const {markedAsHandled, markedAsDone, notes, taskNotDoneReason} = tasks[0].careTask;

    reset({
      markedAsDone: markedAsHandled ? (markedAsDone ? 'done' : 'not-done') : undefined,
      notes,
      taskNotDoneReason,
    });
  }, [reset, tasks]);

  const handleBulkTasksUpdate = () => {
    if (!tasks) return;

    const requests: Promise<CareTask>[] = [];
    const {markedAsDone, taskNotDoneReason, notes} = getValues();

    setSaving(true);

    let putRequestData: PutEntityCareTask = {
      markedAsDone: markedAsDone === 'done' ? true : false,
      executionDatetime: moment().toISOString(),
      notes: notes || null,
    };

    if (!putRequestData.markedAsDone) putRequestData.taskNotDoneReason = taskNotDoneReason;

    tasks.forEach((task) =>
      requests.push(
        EntityUserCareTasksRepository.instance.HandleCareTask(
          entityId,
          task.careTask.userId,
          task.careTask.ref,
          putRequestData
        )
      )
    );

    Promise.all<Promise<CareTask>[]>(requests)
      .then(() => {
        snackBarContext.setDailyCareLogToastNotification({
          severity: 'success',
          message: t(
            translations.daily_care_log.grouped_tasks_handling.snackbar.updating_tasks.success
          ),
        });
        setSaving(false);

        history.push(
          `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Shift}/${entityShiftId}?date=${date}#grouped`
        );
      })
      .catch(() => {
        setSaving(false);
        snackBarContext.setDailyCareLogToastNotification({
          severity: 'error',
          message: t(
            translations.daily_care_log.grouped_tasks_handling.snackbar.updating_tasks.error
          ),
        });
      });
  };

  const [isMarkedAsDone, notDoneReason, notesValue] = watch([
    'markedAsDone',
    'taskNotDoneReason',
    'notes',
  ]);

  const displayNotDoneReason = useMemo(() => isMarkedAsDone === 'not-done', [isMarkedAsDone]);

  const notesAsMandatory = useMemo(
    () => Boolean(isMarkedAsDone === 'not-done' && notDoneReason === 'other'),
    [isMarkedAsDone, notDoneReason]
  );

  // Get tasks information on mount
  useEffect(() => {
    if (location.state) {
      setTasks((location.state as LocationState).tasks);
      setIsLoading(false);
    } else {
      fetchCareTasksInformation();
    }
  }, [location.state, fetchCareTasksInformation]);

  // Fill form async values
  useEffect(() => {
    fillFormValues();
  }, [reset, fillFormValues, tasks]);

  // Notes error explicit behaviour
  useEffect(() => {
    if (!notesAsMandatory && errors.notes?.message) {
      clearErrors('notes');
    } else if (notesAsMandatory && !errors.notes?.message && notesValue?.length === 0) {
      setError(
        'notes',
        {
          message: t(
            translations.care_and_activities.handle_task_modal.observation_required_message
          ),
        },
        {shouldFocus: true}
      );
      notesWrapper.current.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
  }, [
    clearErrors,
    errors.notes?.message,
    notesAsMandatory,
    notesValue?.length,
    setError,
    setFocus,
    t,
  ]);

  // Handle navigation UI state
  useEffect(() => {
    dailyCareLogContext.toggleBottomNavigation(false);
    dailyCareLogContext.setNavigationHeader(true, t(translations.daily_care_log.generics.back));

    return () => {
      // Reset bottom navigation visibility
      dailyCareLogContext.toggleBottomNavigation(true);
      dailyCareLogContext.setNavigationHeader(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading)
    return (
      <Stack gap={1} flexDirection="column">
        <Skeleton
          sx={{
            height: '50px',
            width: '100%',
          }}
        />
        <Skeleton
          sx={{
            height: '50px',
            width: '100%',
          }}
        />
        <Stack gap={4} flexDirection="row" width="100%">
          <Skeleton
            sx={{
              height: '50px',
              width: '50%',
            }}
          />
          <Skeleton
            sx={{
              height: '50px',
              width: '50%',
            }}
          />
        </Stack>
        <Skeleton
          sx={{
            height: '80px',
            width: '100%',
          }}
        />
      </Stack>
    );

  return (
    <Stack direction="column" gap="32px">
      {tasks ? (
        <Typography sx={titleSx}>
          {moment(tasks[0]?.careTask.startsAt).format('H[h]mm')}{' '}
          <Typography
            component="span"
            sx={{
              ...titleSx,
              fontWeight: 500,
            }}
          >
            {tasks[0]?.activityTypeName}
          </Typography>
        </Typography>
      ) : (
        <Skeleton sx={titleLoadingSkeletonSx} />
      )}

      <Typography sx={{...titleSx, fontWeight: 500}}>
        {tasksRefs.length > 1
          ? t(translations.daily_care_log.grouped_tasks_details.select_bottom_bar.count_plural, {
              count: tasksRefs.length,
            })
          : t(translations.daily_care_log.grouped_tasks_details.select_bottom_bar.count, {
              count: 1,
            })}
      </Typography>

      <Stack className="handle-task-form" component="form">
        {/**
         * Mark as done
         */}
        <Controller
          name="markedAsDone"
          control={control}
          rules={{
            required: true,
          }}
          defaultValue={undefined}
          render={({field}) => (
            <RadioGroup row {...field} value={field.value !== undefined ? field.value : ''}>
              <RadioLabel
                value="done"
                control={<Radio />}
                label={t(translations.care_and_activities.handle_task_modal.task_status.done)}
                checked={field.value === 'done'}
              />

              <RadioLabel
                value="not-done"
                control={<Radio />}
                label={t(translations.care_and_activities.handle_task_modal.task_status.not_done)}
                checked={field.value === 'not-done'}
              />
            </RadioGroup>
          )}
        />

        {/**
         * Not Done - reason
         */}
        <Collapse in={displayNotDoneReason} unmountOnExit>
          <Controller
            name="taskNotDoneReason"
            control={control}
            rules={{
              required: getValues('markedAsDone') === 'not-done',
            }}
            render={({field: {value, onBlur, onChange}}) => (
              <DailyCareSelect
                value={value}
                label={t(translations.daily_care_log.task_details.task_not_done_reason)}
                options={Object.values(CareTaskNotDoneReason).map((option) => {
                  return {
                    key: option,
                    label: t(translations.care_taks_not_done_reasons[option]),
                  };
                })}
                {...{onBlur, onChange}}
              />
            )}
          />
        </Collapse>

        {/**
         * Notes
         */}
        <Box ref={notesWrapper}>
          <DailyCareInput
            {...register('notes', {
              required: {
                value: notesAsMandatory,
                message: t(
                  translations.care_and_activities.handle_task_modal.observation_required_message
                ),
              },
            })}
            placeholder={t(translations.daily_care_log.task_details.add_a_note)}
            fullWidth
            multiline
            maxRows={4}
            required={notesAsMandatory}
            errors={errors.notes?.message}
            defaultValue=""
            sx={{paddingX: 1.5, paddingY: 2}}
          />
        </Box>

        <Stack className="action-buttons-container">
          <Stack className="action-buttons">
            <Button
              variant="text"
              size="large"
              color="error"
              onClick={() => history.goBack()}
              disabled={!isDirty}
            >
              {t(translations.daily_care_log.task_details.cancel)}
            </Button>
            <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={handleBulkTasksUpdate}
              disabled={!isValid || saving}
              startIcon={
                saving && <CircularProgress size={16} style={{color: 'var(--dcm-gray-light)'}} />
              }
            >
              {t(translations.daily_care_log.task_details.register)}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default GroupedTasksHandlingScreen;
